/**
 * Тела и ответы маршрутов прогонов мониторинга. docs/phase-2/01-monitoring.md §12
 * (нормативный спек маршрутов), §7.5 (здоровье), §7.8 (лизинг), docs/sources.md
 * ПРАВИЛО 6.1 (курсор) и 6.17 (переполнение фида).
 *
 * Пять POST здесь — единственный способ, которым сервис мониторинга меняет состояние:
 * БД пишет только бэкенд (05-seams §8.1 Р-C8.1). Поэтому отчёт о фетчах несёт не команды,
 * а НАБЛЮДЕНИЯ (исход, байты, самый старый элемент фида, предложение курсора), а решения —
 * здоровье, окно попыток, клэмп курсора — принимает маршрут.
 *
 * Полей отпечатка в материалах нет по той же причине, что в теме (api/intake.ts):
 * ключи уникальности считает бэкенд, теми же функциями `@smm/contracts/node`.
 *
 * Потолки батчей проверяются МАРШРУТОМ (413), а не схемой: 400 сказал бы «тело кривое»
 * там, где тело правильное, а элементов слишком много — тот же принцип, что у
 * EVIDENCE_EXCERPT_TOTAL_MAX (api/intake.ts).
 */

import * as z from 'zod';
import { BRAND_RE } from '../approval-key/limits.ts';
import { UUID_V7_RE } from '../ids.ts';
import { PARSER_KINDS } from '../sources/parser.ts';
import { ISO_UTC_MS } from '../time.ts';
import { codePointsString, IntakeTopicMaterialSchema } from './intake.ts';

const Uuid = z.string().regex(UUID_V7_RE);
const IsoUtcMs = z.string().regex(ISO_UTC_MS);

/** ≤200 элементов в отчёте о фетчах (01-monitoring §12.4). */
export const RUN_FETCH_BATCH_MAX = 200;
/** ≤500 материалов в батче (01-monitoring §12.4). */
export const RUN_MATERIALS_BATCH_MAX = 500;
/** Потолок страницы окна дедупа: 4 МиБ (01-monitoring §12.3). */
export const DEDUP_PAGE_MAX_BYTES = 4_194_304;

/** Назначение запроса — словарь CHECK `source_fetch.purpose` (0005 §4). */
export const SOURCE_FETCH_PURPOSES = ['feed', 'page', 'article', 'probe'] as const;
export type SourceFetchPurpose = (typeof SOURCE_FETCH_PURPOSES)[number];

/**
 * Исходы фетча — словарь CHECK `source_fetch.outcome` (0005 §4). Шире `FetchOutcome`
 * контракта фетчера: `parse_error`, `stub_detected`, `empty_at_200` и `not_this_parser`
 * рождаются на стадии разбора, а не сети, но в отчёте они РАВНОПРАВНЫ — окно попыток
 * здоровья считает заглушку неуспехом (§7.5: источник, стабильно отдающий заглушку,
 * не имеет права вечно оставаться здоровым).
 */
export const SOURCE_FETCH_OUTCOMES = [
  'ok',
  'empty_at_200',
  'http_error',
  'network_error',
  'parse_error',
  'stub_detected',
  'timeout',
  'too_large',
  'redirected',
  'not_this_parser',
] as const;
export type SourceFetchOutcome = (typeof SOURCE_FETCH_OUTCOMES)[number];

/* ─────────────────────────── открыть прогон (§12.1) ─────────────────────── */

export const RunOpenBodySchema = z.strictObject({
  brand: z.string().regex(BRAND_RE),
  /** Единственный вид, который открывается по HTTP: остальные прогоны — джобы бэкенда. */
  kind: z.literal('monitoring'),
  /** Когда сервис НАЧАЛ прогон — до HTTP-вызова. Бэкенд клэмпит будущее своим now. */
  started_at: IsoUtcMs,
  /** host/pid — для разбора «кто держал лизинг» (§7.8). */
  worker_id: z.string().min(1).max(128),
  /**
   * Заявка на мониторинг (api/monitoring-requests.ts), ради которой прогон открыт. Есть —
   * значит прогон РУЧНОЙ: бэкенд связывает его с заявкой при открытии и не двигает по нему
   * ожидание watchdog регулярного прогона (ПРАВИЛО 6.23 сторожит расписание, а не кнопку).
   * Нет — прогон по расписанию, как и раньше.
   */
  request_id: Uuid.optional(),
});
export type RunOpenBody = z.output<typeof RunOpenBodySchema>;

/**
 * Курсор одного источника на момент открытия прогона (ПРАВИЛО 6.1 docs/sources.md).
 *
 * Миллисекунды, а не ISO, — намеренно и по тому же основанию, что у `DedupWindowItem`:
 * это ЧТЕНИЕ колонки `source.cursor_at_ms`, а не момент, о котором отчитывается сторона.
 * Курсор сравнивается с `oldest_item_at` фида (ПРАВИЛО 6.17) и с датами материалов —
 * все три сравнения числовые, и ISO здесь означал бы разбор строки на каждом элементе фида.
 */
export const RunCursorSchema = z.strictObject({
  source_code: z.string().min(1).max(64),
  /** null — источник ещё не отдавал сохранённых материалов: обход идёт от окна lookback. */
  cursor_at_ms: z.int().nonnegative().nullable(),
});
export type RunCursor = z.output<typeof RunCursorSchema>;

/** Отклонение одного источника из интерфейса — см. `RunOpened.source_overrides`. */
export const RunSourceOverrideSchema = z.strictObject({
  source_code: z.string().min(1).max(64),
  weight: z.number().gt(0).lte(1).nullable(),
  enabled: z.boolean().nullable(),
});
export type RunSourceOverride = z.output<typeof RunSourceOverrideSchema>;

export const RunOpenedSchema = z.strictObject({
  run_id: Uuid,
  /** До этого момента лизинг жив; heartbeat продлевает (§7.8). */
  lease_expires_at: IsoUtcMs,
  /**
   * Курсоры ВСЕХ включённых источников бренда (действующая включённость пары: отклонение из
   * интерфейса ?? `brand_source.enabled`, 0021). Отдаются
   * на открытии, потому что у мониторинга базы нет, а второй дом курсора — локальный файл
   * состояния — означал бы, что клэмп маршрута (ПРАВИЛО 6.1: только вперёд, не дальше
   * реально сохранённого) можно обойти, просто не спросив бэкенд.
   *
   * Без этого чтения ПРАВИЛО 6.17 (переполнение фида) невычислимо: сравнивать
   * `oldest_item_at` не с чем.
   */
  cursors: z.array(RunCursorSchema),
  /**
   * Отклонения веса и включённости, поставленные из интерфейса (0021, api/source-controls.ts).
   * Только поля, которые РУКАМИ отличаются от реестра: `null` — «как в реестре». Реестр
   * мониторинг читает своим файлом, и правило `отклонение ?? реестр` он применяет к СВОЕМУ
   * файлу — поэтому бэкенд шлёт отклонения, а не готовые значения: реестровую половину он
   * знает лишь проекцией, которая может отстать от файла до следующей синхронизации.
   *
   * Отдаются здесь, а не отдельным маршрутом, по той же причине, что курсоры: прогон обязан
   * видеть ОДНО состояние источников от открытия до закрытия, а повтор ключа открытия
   * возвращает тот же ответ целиком (кэш идемпотентности).
   *
   * Необязательное поле: бэкенд до 0021 его не шлёт, и мониторинг тогда идёт по реестру как
   * есть. Вес — та же область (0, 1], что у реестра: подставленный вместо реестрового, он
   * обязан ломать ранжирование не больше, чем мог бы файл.
   */
  source_overrides: z.array(RunSourceOverrideSchema).optional(),
});
export type RunOpened = z.output<typeof RunOpenedSchema>;

/* ───────────────────────── heartbeat и закрытие (§12.2) ─────────────────── */

export const RunHeartbeatBodySchema = z.strictObject({
  /** Числа воронки «на сейчас»; уезжают в watchdog_expectation.last_stats_json. */
  stats: z.record(z.string().max(64), z.number()).optional(),
});
export type RunHeartbeatBody = z.output<typeof RunHeartbeatBodySchema>;

/**
 * Счётчики закрытия. Все опциональны: NFR-4 требует закрывать и прогон, упавший на
 * середине, — у него есть только часть чисел. Незаданный счётчик колонку не затирает.
 */
export const RunCountersSchema = z.strictObject({
  sources_total: z.int().nonnegative().optional(),
  sources_ok: z.int().nonnegative().optional(),
  sources_dead: z.int().nonnegative().optional(),
  sources_degraded: z.int().nonnegative().optional(),
  materials_seen: z.int().nonnegative().optional(),
  materials_passed_heuristic: z.int().nonnegative().optional(),
  materials_passed_llm: z.int().nonnegative().optional(),
  materials_deep: z.int().nonnegative().optional(),
  topics_created: z.int().nonnegative().optional(),
  bytes_downloaded: z.int().nonnegative().optional(),
  llm_calls: z.int().nonnegative().optional(),
  cost_micro_usd: z.int().nonnegative().optional(),
  cache_reads: z.int().nonnegative().optional(),
});
export type RunCounters = z.output<typeof RunCountersSchema>;

export const RunCloseBodySchema = z
  .strictObject({
    finished_at: IsoUtcMs,
    outcome: z.enum(['ok', 'partial', 'failed']),
    counters: RunCountersSchema,
    /**
     * Обещание «когда ждать следующий прогон» — источник правды для watchdog
     * (ПРАВИЛО 6.23 docs/sources.md: пишет продюсер по факту завершения, не конфиг).
     * null допустим ТОЛЬКО при outcome='failed': незавершённый прогон отметку не двигает.
     */
    next_expected_at: IsoUtcMs.nullable(),
    error_text: z.string().max(2000).nullable(),
    /** В job_run.stats_json — то, чего нет в счётчиках monitoring_run (Р-C8.2). */
    stats: z.record(z.string().max(64), z.number()).optional(),
  })
  .check((ctx) => {
    const v = ctx.value;
    if (v.outcome !== 'failed' && v.next_expected_at === null) {
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['next_expected_at'],
        message: 'next_expected_at_required_unless_failed',
      });
    }
  });
export type RunCloseBody = z.output<typeof RunCloseBodySchema>;

/* ───────────────────────── отчёт о фетчах (§12.4) ───────────────────────── */

/**
 * Один запрос к источнику — одна строка. Зеркало колонок `source_fetch` (0005 §4)
 * плюс два наблюдения, по которым бэкенд двигает `source`:
 *  - `oldest_item_at` — самый старый элемент фида (ПРАВИЛО 6.17: сравнение с курсором
 *    ловит провернувшийся фид; сравнивает и алертит МОНИТОРИНГ, бэкенд только хранит);
 *  - `cursor_at` — ПРЕДЛОЖЕНИЕ нового курсора (ПРАВИЛО 6.1). Клиент считает его по
 *    материалам, УСПЕШНО отправленным в бэкенд (и по fetched_at при date_semantics:
 *    'modified'); бэкенд клэмпит: никогда назад, не позже now и не дальше максимума
 *    реально сохранённых материалов источника.
 */
export const RunFetchReportSchema = z
  .strictObject({
    source_code: z.string().min(1).max(64),
    parser: z.enum(PARSER_KINDS),
    purpose: z.enum(SOURCE_FETCH_PURPOSES),
    /** Обязателен при purpose != 'feed': адрес фида знает реестр, адрес статьи — только отчёт. */
    url: z.string().min(1).max(2048).nullable(),
    started_at: IsoUtcMs,
    finished_at: IsoUtcMs.nullable(),
    http_status: z.int().min(100).max(599).nullable(),
    outcome: z.enum(SOURCE_FETCH_OUTCOMES),
    items_found: z.int().nonnegative(),
    items_new: z.int().nonnegative(),
    error_text: z.string().max(2000).nullable(),
    /** Байты ПОТОКА, не Content-Length (грабля 16.2) → колонка bytes_fetched. */
    bytes: z.int().nonnegative(),
    /** Финальный адрес фактически полученного ответа (детект подмены t.me/s/). */
    final_url: z.string().min(1).max(2048).nullable(),
    /** Location непройденного редиректа (outcome='redirected'). */
    redirect_location: z.string().min(1).max(2048).nullable(),
    /** 1..3 — ПРАВИЛО 6.16: три попытки с backoff внутри прогона, не больше. */
    attempt_no: z.int().min(1).max(3),
    oldest_item_at: IsoUtcMs.nullable(),
    cursor_at: IsoUtcMs.nullable(),
  })
  .check((ctx) => {
    const v = ctx.value;
    if (v.purpose !== 'feed' && v.url === null) {
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['url'],
        message: 'url_required_unless_feed',
      });
    }
  });
export type RunFetchReport = z.output<typeof RunFetchReportSchema>;

export const RunFetchesBodySchema = z.strictObject({
  /** Потолок RUN_FETCH_BATCH_MAX держит маршрут (413), не схема. */
  fetches: z.array(RunFetchReportSchema).min(1),
});
export type RunFetchesBody = z.output<typeof RunFetchesBodySchema>;

export const RunFetchesAcceptedSchema = z.strictObject({
  accepted: z.int().nonnegative(),
});
export type RunFetchesAccepted = z.output<typeof RunFetchesAcceptedSchema>;

/* ───────────────────────── оценка материала (docs/release-2026-09-19.md §3.1) ──── */

/** Потолки текстовых полей оценки — зеркало CHECK'ов `monitoring_item` (0019), в кодпоинтах. */
export const MONITORING_GIST_MAX = 300;
export const MONITORING_WHY_MAX = 500;
export const MONITORING_ANGLE_MAX = 500;

/**
 * Что мониторинг решил о материале В ЭТОМ ПРОГОНЕ. Едет полем материала батча, а не
 * отдельным маршрутом: батч уходит один раз, после последнего вызова модели, и именно в
 * этот момент известно всё — вердикт стадии 1, вердикт стадии 2 и балл ранжирования.
 *
 * `stage` — САМАЯ ГЛУБОКАЯ стадия, до которой материал дошёл: `deep` — модель прочитала
 * полный текст, `prefilter` — только заголовок и лид. Отсеянные эвристикой стадии 0 оценки
 * не несут вовсе (`assessment: null`): модель их не видела, и число «рейтинга» у них было бы
 * выдумкой.
 *
 * `rating` — ЕДИНСТВЕННАЯ шкала, которую видит человек, и считается она здесь, у
 * мониторинга, один раз (§3.2):
 *   • `deep` — `relevance` стадии 2 («релевантность бренду по полному тексту»);
 *   • `prefilter` — вероятность релевантности по стадии 1: `confidence` при `relevant`,
 *     `1 − confidence` при отказе (`confidence` — уверенность в решении, а не в релевантности).
 * `relevant` — итоговый вердикт той же стадии: `keep` стадии 2 или `relevant` стадии 1.
 *
 * `gist` — одна строка по-русски о том, ЧТО в материале (факт, не оценка): модель пишет её на
 * стадии 1 вместе с вердиктом. `why` — почему решение такое (глубже — стадия 2).
 */
export const MonitoringAssessmentSchema = z
  .strictObject({
    stage: z.enum(['prefilter', 'deep']),
    relevant: z.boolean(),
    rating: z.number().min(0).max(1),
    confidence: z.number().min(0).max(1).nullable(),
    relevance: z.number().min(0).max(1).nullable(),
    /** Балл ранжирования (relevance × свежесть × вес источника); `null` — до ранжирования не дошёл. */
    score: z.number().min(0).max(1).nullable(),
    topic_class: z.string().min(1).max(64).nullable(),
    gist: codePointsString(1, MONITORING_GIST_MAX).nullable(),
    why: codePointsString(1, MONITORING_WHY_MAX).nullable(),
    angle: codePointsString(1, MONITORING_ANGLE_MAX).nullable(),
  })
  .check((ctx) => {
    const v = ctx.value;
    if (v.stage === 'prefilter' && v.confidence === null) {
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['confidence'],
        message: 'prefilter_requires_confidence',
      });
    }
    if (v.stage === 'deep' && v.relevance === null) {
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['relevance'],
        message: 'deep_requires_relevance',
      });
    }
  });
export type MonitoringAssessment = z.output<typeof MonitoringAssessmentSchema>;

/* ───────────────────────── батч материалов (§12.4) ──────────────────────── */

/**
 * Материал батча — ТА ЖЕ форма, что материал темы (05-seams §2.5: «форма тела там же,
 * что здесь у materials, и переиспользует те же функции»), минус `role` (роль есть только
 * внутри темы) и плюс то, что у обхода есть всегда, а у темы не спрашивается:
 * источник обязателен, сырая дата, язык, длина полного текста и вердикт префильтра —
 * отсеянные материалы тоже хранятся (01-monitoring §9.5), это память дедупа и
 * единственный материал для калибровки каскада.
 */
export const RunMaterialSchema = IntakeTopicMaterialSchema.omit({ role: true }).extend({
  /** У материала обхода источник есть всегда — в отличие от темы подфазы R1. */
  source_code: z.string().min(1).max(64),
  /** Дата, как она выглядела в источнике, — разбор инцидентов и тест-векторы (§11.3). */
  published_at_raw: z.string().min(1).max(128).nullable(),
  lang: z
    .string()
    .regex(/^[a-z]{2}$/)
    .nullable(),
  /** Длина полного текста; сам текст в БД не живёт (§11.7). */
  body_chars: z.int().nonnegative().nullable(),
  prefilter_outcome: z
    .enum(['dropped_heuristic', 'dropped_llm', 'dropped_deep', 'passed'])
    .nullable(),
  /** Код отсева стадии 0 (`dup_exact`, `out_of_window`, …) — калибровка каскада (§11.3). */
  decision_reason: z.string().min(1).max(200).nullable(),
  /**
   * Оценка материала в этом прогоне (0019 `monitoring_item`); `null` — модель материал не
   * видела (отсев стадии 0). Необязательна на входе: батч прежнего мониторинга остаётся
   * валидным и просто не наполняет таблицу прогона.
   */
  assessment: MonitoringAssessmentSchema.nullable().default(null),
});
export type RunMaterial = z.output<typeof RunMaterialSchema>;

export const RunMaterialsBodySchema = z.strictObject({
  /** Потолок RUN_MATERIALS_BATCH_MAX держит маршрут (413), не схема. */
  materials: z.array(RunMaterialSchema).min(1),
});
export type RunMaterialsBody = z.output<typeof RunMaterialsBodySchema>;

/**
 * `accepted` — вставлено новых, `duplicates` — конфликтов ON CONFLICT DO NOTHING;
 * сумма равна длине батча. Ненулевые duplicates при чистом прогоне — метрика
 * «дедуп мониторинга не сработал», а не ошибка (§12.4).
 */
export const RunMaterialsAcceptedSchema = z.strictObject({
  accepted: z.int().nonnegative(),
  duplicates: z.int().nonnegative(),
});
export type RunMaterialsAccepted = z.output<typeof RunMaterialsAcceptedSchema>;

/* ───────────────────────── окно дедупа (§12.3) ──────────────────────────── */

/**
 * Ни заголовков, ни текстов — только то, что нужно для сравнения (§12.3).
 * simhash — парой uint32, не одним числом: better-sqlite3 и JSON отдают JS-number,
 * а выше 2^53 точность теряется (§11.3); пара выводится из hex `lead_simhash`.
 */
export const DedupWindowItemSchema = z.strictObject({
  fingerprint: z.string().regex(/^[0-9a-f]{64}$/),
  simhash_hi: z.int().nonnegative(),
  simhash_lo: z.int().nonnegative(),
  url_canonical: z.string(),
  published_at_ms: z.int().nonnegative(),
  /** null — терминального решения ещё нет; окно 8 недель отсчитывается от него (6.19). */
  terminal_at_ms: z.int().nonnegative().nullable(),
});
export type DedupWindowItem = z.output<typeof DedupWindowItemSchema>;

/**
 * Страница окна. Пагинация — только `since_ms` (форма §12.3): элементы идут по
 * возрастанию `published_at_ms`, клиент продолжает с `since_ms = published_at_ms`
 * последнего элемента (сравнение строгое `>`); страница режется по потолку 4 МиБ на
 * ГРАНИЦЕ значения `published_at_ms`, чтобы строгое `>` не теряло соседей по
 * миллисекунде. Пустой `items` означает «окно дочитано».
 */
export const DedupWindowPageSchema = z.strictObject({
  window_days: z.int().positive(),
  generated_at: IsoUtcMs,
  items: z.array(DedupWindowItemSchema),
});
export type DedupWindowPage = z.output<typeof DedupWindowPageSchema>;

/* ───────────────────────── давление слотов (§12.5) ──────────────────────── */

/**
 * Единственное окно мониторинга в контур согласования: три числа и ни одного текста.
 * `evergreen_needed = max(0, slots_left_this_week − work_in_flight)` считает БЭКЕНД —
 * он один знает, что такое «в работе» (§10.4).
 */
export const SlotPressureSchema = z.strictObject({
  slots_left_this_week: z.int().nonnegative(),
  work_in_flight: z.int().nonnegative(),
  evergreen_needed: z.int().nonnegative(),
});
export type SlotPressure = z.output<typeof SlotPressureSchema>;
